import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionStatus } from '../../core/models/mentor-panel/mentor-panel-api-models';
import { SessionMeetingStatus } from '../../constants/session-meeting-status.enum';

@Component({
    selector: 'app-status-badge',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './status-badge.component.html',
    styleUrl: './status-badge.component.scss'
})
export class StatusBadgeComponent {
    @Input() status!: SessionStatus | SessionMeetingStatus | string;

    private readonly labels: Partial<Record<SessionStatus, string>> = {
        [SessionStatus.AWAITING_ACCEPTANCE]: 'Pending',
        [SessionStatus.ACCEPTED]: 'Accepted',
        [SessionStatus.COMPLETED]: 'Completed',
        [SessionStatus.REJECTED]: 'Rejected',
        [SessionStatus.CANCELLED]: 'Cancelled',
        [SessionStatus.PENDING_PAYMENT]: 'Pending Payment',
        [SessionStatus.CANCELLED_BY_MENTOR]: 'Cancelled by Mentor',
        [SessionStatus.CANCELLED_BY_STUDENT]: 'Cancelled by Student',
        [SessionStatus.EXPIRED]: 'Expired',
        [SessionStatus.ALL]: 'All',
        [SessionStatus.WAITING]: 'Waiting',
        [SessionStatus.COMPLETE]: 'Completed',
        [SessionStatus.REFUND_PROCESSING]: 'Refund Processing',
        [SessionStatus.REFUNDED]: 'Refunded',
        [SessionStatus.MISSED_SESSION]: 'Missed Session'
    };

    private readonly classMap: Partial<Record<SessionStatus, string>> = {
        [SessionStatus.AWAITING_ACCEPTANCE]: 'badge-pending',
        [SessionStatus.ACCEPTED]: 'badge-accepted',
        [SessionStatus.COMPLETED]: 'badge-completed',
        [SessionStatus.REJECTED]: 'badge-rejected',
        [SessionStatus.CANCELLED]: 'badge-cancelled',
        [SessionStatus.PENDING_PAYMENT]: 'badge-pending',
        [SessionStatus.CANCELLED_BY_MENTOR]: 'badge-cancelled',
        [SessionStatus.CANCELLED_BY_STUDENT]: 'badge-cancelled',
        [SessionStatus.EXPIRED]: 'badge-cancelled',
        [SessionStatus.WAITING]: 'badge-pending',
        [SessionStatus.COMPLETE]: 'badge-completed',
        [SessionStatus.REFUND_PROCESSING]: 'badge-pending',
        [SessionStatus.REFUNDED]: 'badge-cancelled',
        [SessionStatus.MISSED_SESSION]: 'badge-rejected'
    };

    get label(): string {
        const s = (this.status || '').toLowerCase().replace(/_/g, ' ');
        // Return mapped label if found
        for (const [key, value] of Object.entries(this.labels)) {
            if (key.toLowerCase() === s) return value;
        }
        // Fallback to title case
        return this.labels[s as SessionStatus] || (s.charAt(0).toUpperCase() + s.slice(1));
    }

    get badgeClass(): string {
        const s = (this.status || '').toLowerCase().replace(/_/g, ' ');

        // Explicit variations
        if (s === 'completed' || s === 'complete' || s === 'done') return 'badge-completed';
        if (s === 'accepted' || s === 'confirmed' || s === 'approved') return 'badge-accepted';
        if (s === 'rejected' || s === 'denied') return 'badge-rejected';
        if (s === 'cancelled' || s === 'expired' || s.includes('cancelled by')) return 'badge-cancelled';
        if (s === 'awaiting acceptance' || s === 'pending' || s === 'pending payment' || s === 'waiting' || s === 'refund processing') return 'badge-pending';
        if (s === 'today') return 'badge-accepted';
        if (s === 'missed session' || s === 'missed') return 'badge-rejected';
        if (s === 'refunded') return 'badge-cancelled';

        // Partial match fallback
        if (s.includes('complete')) return 'badge-completed';
        if (s.includes('accept')) return 'badge-accepted';
        if (s.includes('reject')) return 'badge-rejected';
        if (s.includes('cancel')) return 'badge-cancelled';
        if (s.includes('pending') || s.includes('awaiting')) return 'badge-pending';
        if (s.includes('missed')) return 'badge-rejected';

        return 'badge-pending';
    }
}
